import React, { FC, FormEvent, useState } from 'react';
import {
  useLicenseActions, useProfessionalLicenses, useTeamConnectionActions, useTeamConnections,
} from '../../../hooks/api/Team/useTeam';
import {
  CONNECTION_STATUS_LABELS, getErrorMessages, LICENSE_STATUS_LABELS, LICENSE_TYPE_LABELS, LicenseType,
  TeamConnection,
} from '../../../utility/team';
import { classNames } from '../../../utility/utility';
import { useSignature } from '../../../hooks/api/Signature/useSignature';

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

const statusColor = (status: string) => {
  if (status === 'approved' || status === 'active') return 'bg-green-50 text-green-700';
  if (status === 'rejected' || status === 'declined') return 'bg-red-50 text-red-700';
  return 'bg-yellow-50 text-yellow-800';
};

const StatusBadge: FC<{ status: string; label: string }> = ({ status, label }) => (
  <span className={classNames('inline-flex items-center rounded-md px-2 py-1 text-xs font-medium', statusColor(status))}>{label}</span>
);

const ErrorList: FC<{ error: unknown }> = ({ error }) =>
  error ? <ul className="mt-2 text-sm text-red-600">{getErrorMessages(error).map((m) => <li key={m}>{m}</li>)}</ul> : null;

const LicenseSection: FC = () => {
  const { data: licenses, isLoading } = useProfessionalLicenses();
  const { add, remove } = useLicenseActions();
  const [ licenseType, setLicenseType ] = useState<LicenseType>('real_estate');
  const [ state, setState ] = useState('');
  const [ licenseNumber, setLicenseNumber ] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await add.mutateAsync({ license_type: licenseType, state, license_number: licenseNumber });
      setState('');
      setLicenseNumber('');
    } catch {
      // the error is shown below from the mutation state
    }
  };

  return (
    <section aria-labelledby="license-heading">
      <h2 id="license-heading" className="text-base font-semibold leading-6 text-gray-900">Your professional license</h2>
      <p className="mt-2 text-sm text-gray-700">
        Only needed if you will create offers for someone else. A real estate or law license must be approved by REIX
        before you can act for another buyer. Using the tool for your own offers needs no license.
      </p>

      <ul className="mt-4 divide-y divide-gray-100 sm:max-w-xl">
        {!isLoading && licenses.length === 0 && <li className="py-2 text-sm text-gray-500">No license added yet.</li>}
        {licenses.map((license) => (
          <li key={license.id} className="flex items-center justify-between gap-x-4 py-3">
            <div className="text-sm">
              <p className="font-medium text-gray-900">{LICENSE_TYPE_LABELS[license.license_type]} - {license.state} #{license.license_number}</p>
              {license.status === 'rejected' && license.rejection_reason && (
                <p className="text-red-600">{license.rejection_reason}</p>
              )}
            </div>
            <div className="flex items-center gap-x-3">
              <StatusBadge status={license.status} label={LICENSE_STATUS_LABELS[license.status]} />
              <button type="button" className="text-sm text-gray-500 hover:text-red-600" onClick={() => remove.mutate(license.id)}>
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>

      <form onSubmit={handleSubmit} className="mt-4 grid gap-3 sm:max-w-xl sm:grid-cols-4">
        <div className="sm:col-span-2">
          <label htmlFor="license_type" className="block text-sm font-medium text-gray-900">License type</label>
          <select id="license_type" className={inputClass} value={licenseType} onChange={(e) => setLicenseType(e.target.value as LicenseType)}>
            {Object.entries(LICENSE_TYPE_LABELS).map(([ value, label ]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="license_state" className="block text-sm font-medium text-gray-900">State</label>
          <input id="license_state" className={inputClass} maxLength={2} placeholder="TX" value={state} onChange={(e) => setState(e.target.value)} />
        </div>
        <div>
          <label htmlFor="license_number" className="block text-sm font-medium text-gray-900">License number</label>
          <input id="license_number" className={inputClass} value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
        </div>
        <div className="sm:col-span-4">
          <button type="submit" disabled={add.isPending || !state || !licenseNumber} className={primaryButton}>Submit for review</button>
          <ErrorList error={add.error} />
        </div>
      </form>
    </section>
  );
};

const ConnectionRow: FC<{ connection: TeamConnection }> = ({ connection }) => {
  const { accept, decline, end, signing } = useTeamConnectionActions();
  const { signature } = useSignature();
  const busy = accept.isPending || decline.isPending || end.isPending || signing.isPending;
  const canGrantSigning = connection.status === 'active' && connection.my_role === 'buyer';
  const roleText = connection.my_role === 'buyer' ? 'is your agent' : 'is a buyer you act for';
  const canEnd = connection.status === 'active' || (connection.status === 'pending' && connection.initiated_by_me);

  return (
    <li className="flex items-center justify-between gap-x-4 py-3">
      <div className="text-sm">
        <p className="font-medium text-gray-900">{connection.other_user.name}</p>
        <p className="text-gray-500">{connection.other_user.email} - {roleText}</p>
        {connection.status === 'pending' && connection.initiated_by_me && <p className="text-gray-500">Waiting for them to accept.</p>}
        {canGrantSigning && (
          <div className="mt-2">
            <label className="flex items-center gap-x-2 text-gray-700">
              <input
                type="checkbox"
                aria-label={`Let ${connection.other_user.name} apply my signature`}
                checked={connection.signing_authority}
                disabled={busy || (!signature && !connection.signing_authority)}
                onChange={(e) => signing.mutate({ id: connection.id, allowed: e.target.checked })}
              />
              Let {connection.other_user.name} apply my saved signature and initials to contracts they create for me
            </label>
            {!signature && !connection.signing_authority && (
              <p className="text-gray-500">Save your signature first under Configuration &gt; Signatures.</p>
            )}
            <ErrorList error={signing.error} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-x-2">
        <StatusBadge status={connection.status} label={CONNECTION_STATUS_LABELS[connection.status]} />
        {connection.can_accept && (
          <>
            <button type="button" disabled={busy} className={primaryButton} onClick={() => accept.mutate(connection.id)}>Accept</button>
            <button type="button" disabled={busy} className={secondaryButton} onClick={() => decline.mutate(connection.id)}>Decline</button>
          </>
        )}
        {canEnd && (
          <button
            type="button"
            disabled={busy}
            className={secondaryButton}
            onClick={() => {
              const message = connection.status === 'active'
                ? 'End this connection? Offers already created stay on record.'
                : 'Withdraw this request?';
              if (window.confirm(message)) end.mutate(connection.id);
            }}
          >
            {connection.status === 'active' ? 'End' : 'Withdraw'}
          </button>
        )}
      </div>
    </li>
  );
};

const ConnectionsSection: FC = () => {
  const { data: connections, isLoading } = useTeamConnections();
  const { invite } = useTeamConnectionActions();
  const [ email, setEmail ] = useState('');
  const [ role, setRole ] = useState<'agent' | 'buyer'>('agent');
  const [ sent, setSent ] = useState(false);

  const visible = connections.filter((c) => c.status === 'active' || c.status === 'pending');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSent(false);
    try {
      await invite.mutateAsync({ email, role });
      setEmail('');
      setSent(true);
    } catch {
      // the error is shown below from the mutation state
    }
  };

  return (
    <section aria-labelledby="connections-heading" className="mt-12">
      <h2 id="connections-heading" className="text-base font-semibold leading-6 text-gray-900">Team connections</h2>
      <p className="mt-2 text-sm text-gray-700">
        Connect with a licensed professional to let them create auto-contract offers for you, or connect with a buyer
        you act for. Either side can send the request and the other side accepts. The connection only covers
        auto-contracts, and either side can end it at any time.
      </p>

      <ul className="mt-4 divide-y divide-gray-100 sm:max-w-3xl">
        {!isLoading && visible.length === 0 && <li className="py-2 text-sm text-gray-500">No connections yet.</li>}
        {visible.map((connection) => <ConnectionRow key={connection.id} connection={connection} />)}
      </ul>

      <form onSubmit={handleSubmit} className="mt-4 grid gap-3 sm:max-w-3xl sm:grid-cols-3">
        <div>
          <label htmlFor="invite_role" className="block text-sm font-medium text-gray-900">I want to</label>
          <select id="invite_role" className={inputClass} value={role} onChange={(e) => setRole(e.target.value as 'agent' | 'buyer')}>
            <option value="agent">Invite my agent</option>
            <option value="buyer">Act for a buyer</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="invite_email" className="block text-sm font-medium text-gray-900">Their REIX email</label>
          <input id="invite_email" type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="sm:col-span-3">
          <button type="submit" disabled={invite.isPending || !email} className={primaryButton}>Send request</button>
          {sent && <span className="ml-3 text-sm text-green-600">Request sent.</span>}
          <ErrorList error={invite.error} />
        </div>
      </form>
    </section>
  );
};

const TeamPage: FC = () => (
  <div className="px-2">
    <h1 className="text-base font-semibold leading-6 text-gray-900">Team</h1>
    <div className="mt-6"><LicenseSection /></div>
    <ConnectionsSection />
  </div>
);

export default TeamPage;
